import { isScheduledInFuture } from "./helperFns";
import { EMPTY_SCHEDULE, ScheduleValue } from "./scheduleValidation";

/**
 * The fixed content types (Article, Poll, Quiz, Event, VOD, ...) already carry four loose keys for a
 * schedule — in the `settings` object, or flattened onto a screen's own state:
 *
 *   is_schedule_publish, schedule_publish_datetime, is_schedule_unpublish, schedule_unpublish_datetime
 *
 * These two helpers translate between those keys and the control's `ScheduleValue`, so each screen
 * only needs a one-line adapter. KIWI-6576.
 *
 * ⚠️ The keys are UI/form state only. Nothing on the backend turns them into a scheduler trigger —
 * that happens only when `is_schedule` + `schedule_date_time` are sent on the publish call (see
 * `planPublishSteps`). That mismatch is exactly why Poll/Quiz/Event "scheduled" without scheduling.
 */
export type ScheduleKeys = {
  is_schedule_publish?: boolean;
  schedule_publish_datetime?: string | null;
  is_schedule_unpublish?: boolean;
  schedule_unpublish_datetime?: string | null;
};

/** A time worth keeping: a non-empty string. The keys hold `""` or `null` for "not set". */
const timeOrNull = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

export const readSchedule = (source?: ScheduleKeys | null): ScheduleValue => {
  if (!source) return { ...EMPTY_SCHEDULE };
  return {
    isSchedulePublish: Boolean(source.is_schedule_publish),
    schedulePublishDateTime: timeOrNull(source.schedule_publish_datetime),
    isScheduleUnpublish: Boolean(source.is_schedule_unpublish),
    scheduleUnpublishDateTime: timeOrNull(source.schedule_unpublish_datetime),
  };
};

/**
 * Returns a copy of `target` with the four keys set from `value`. Cleared times are written as `""`,
 * which is what the existing screens and the backend `Settings` input already use for "not set".
 */
export const writeSchedule = <T extends object>(
  target: T | null | undefined,
  value: ScheduleValue,
): T &
  Required<Pick<ScheduleKeys, "is_schedule_publish" | "is_schedule_unpublish">> &
  Record<"schedule_publish_datetime" | "schedule_unpublish_datetime", string> => ({
  ...(target as T),
  is_schedule_publish: value.isSchedulePublish,
  schedule_publish_datetime: value.schedulePublishDateTime ?? "",
  is_schedule_unpublish: value.isScheduleUnpublish,
  schedule_unpublish_datetime: value.scheduleUnpublishDateTime ?? "",
});

/** The two listing / single-read fields that carry a pending scheduler trigger (ISO-8601 UTC). */
export type ScheduleTriggers = {
  scheduled_publish_trigger_datetime?: string | null;
  scheduled_unPublish_trigger_datetime?: string | null;
};

export type PendingSchedule = { publish: string | null; unpublish: string | null };

/**
 * The schedules an item already has on the server, read from its trigger fields. Both content
 * families return them on the single-item read (`getCmsContentByPath` / `getSchemaContent`) and on
 * listing rows. A trigger whose time has passed has already run, so it is dropped. KIWI-6762.
 *
 * `initial` seeds the form (the side is ticked with its time) and is what Publish compares against so
 * an existing schedule is never sent again; `pending` tells the control which sides to lock.
 */
export const scheduleFromTriggers = (
  source?: ScheduleTriggers | null,
): { initial: ScheduleValue; pending: PendingSchedule } => {
  const publish = isScheduledInFuture(source?.scheduled_publish_trigger_datetime)
    ? (source?.scheduled_publish_trigger_datetime as string)
    : null;
  const unpublish = isScheduledInFuture(source?.scheduled_unPublish_trigger_datetime)
    ? (source?.scheduled_unPublish_trigger_datetime as string)
    : null;
  return {
    initial: {
      isSchedulePublish: Boolean(publish),
      schedulePublishDateTime: publish,
      isScheduleUnpublish: Boolean(unpublish),
      scheduleUnpublishDateTime: unpublish,
    },
    pending: { publish, unpublish },
  };
};

/**
 * Returns a copy of a screen's form state (or an Article's `settings`) seeded with the schedules the
 * item already has: the four schedule keys the control reads, plus the two trigger times, which mark
 * those sides as existing — read them back with `scheduleFromTriggers(state)` to get `pending` for
 * the control and `existing` for `planContentPublish`. KIWI-6762.
 *
 * ⚠️ Only for state that is NOT spread into a save request: the trigger keys are not document
 * fields, and the backend rejects unknown fields.
 */
export const seedSchedule = <T extends object>(
  target: T | null | undefined,
  source?: ScheduleTriggers | null,
) => {
  const { initial, pending } = scheduleFromTriggers(source);
  return {
    ...writeSchedule(target, initial),
    scheduled_publish_trigger_datetime: pending.publish,
    scheduled_unPublish_trigger_datetime: pending.unpublish,
  };
};
